import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Switch,
  Stack,
  CircularProgress,
  Box,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import CompanyVeiwController from "../../redux/actions/companyApiActions";
import FormDrawer from "../../components/layout/FormDrawer";
import {
  BRAND,
  primaryButtonSx,
  inputSx,
  mobileInputSx,
} from "../../components/auth/authTheme";

import "../../styles/styles.css";
import "../../styles/layout.css";

const validationSchema = Yup.object({
  title: Yup.string().trim().required("Title is required"),
  message: Yup.string().trim().required("Message is required"),
});

function AddAnnouncement({ show, handleClose, initialData = null, refreshList }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = isMobile ? mobileInputSx : inputSx;
  const { AddAnnouncement, updateAnnouncement } = CompanyVeiwController();
  const { isLoding } = useSelector((state) => state.company);

  const isEdit = Boolean(initialData?.id);
  const [initialValues, setInitialValues] = useState({
    title: "",
    message: "",
    active: true,
  });

  useEffect(() => {
    if (!show) return;
    if (initialData) {
      setInitialValues({
        title: initialData.title || "",
        message: initialData.message || "",
        active: initialData.active ?? true,
      });
    } else {
      setInitialValues({ title: "", message: "", active: true });
    }
  }, [show, initialData]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const action = isEdit
        ? updateAnnouncement(initialData.id, values)
        : AddAnnouncement(values);
      await dispatch(action);
      resetForm();
      handleClose();
      refreshList?.();
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnBlur
      validateOnChange={false}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
        submitForm,
      }) => {
        const isSaving = isSubmitting || isLoding;

        return (
          <Form style={{ display: "contents" }}>
            <FormDrawer
              open={show}
              onClose={handleClose}
              title={isEdit ? "Edit Announcement" : "Create Announcement"}
              subtitle={
                isEdit
                  ? "Update announcement details"
                  : "Share a notice with your organization"
              }
              width={{ xs: "100%", sm: 460, md: 480 }}
              footer={
                isMobile ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      disabled={isSaving}
                      onClick={() => submitForm()}
                      fullWidth
                      sx={{
                        ...primaryButtonSx,
                        minHeight: 48,
                        borderRadius: "12px",
                        "&.Mui-disabled": {
                          bgcolor: isSaving ? BRAND.orange : undefined,
                          color: isSaving ? "#fff" : undefined,
                          opacity: isSaving ? 0.85 : undefined,
                        },
                      }}
                      startIcon={
                        isSaving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : null
                      }
                    >
                      {isSaving
                        ? isEdit
                          ? "Updating..."
                          : "Creating..."
                        : isEdit
                          ? "Update"
                          : "Create"}
                    </Button>
                    <Button
                      onClick={handleClose}
                      color="inherit"
                      disabled={isSaving}
                      fullWidth
                      sx={{
                        minHeight: 44,
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Button
                      onClick={handleClose}
                      color="inherit"
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      disabled={isSaving}
                      onClick={() => submitForm()}
                      sx={{
                        ...primaryButtonSx,
                        minWidth: 120,
                        "&.Mui-disabled": {
                          bgcolor: isSaving ? BRAND.orange : undefined,
                          color: isSaving ? "#fff" : undefined,
                          opacity: isSaving ? 0.85 : undefined,
                        },
                      }}
                      startIcon={
                        isSaving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : null
                      }
                    >
                      {isSaving
                        ? isEdit
                          ? "Updating..."
                          : "Creating..."
                        : isEdit
                          ? "Update"
                          : "Create"}
                    </Button>
                  </>
                )
              }
            >
              <Box
                sx={{
                  bgcolor: isMobile ? "#fff" : "transparent",
                  borderRadius: isMobile ? "12px" : 0,
                  border: isMobile ? `1px solid ${BRAND.border}` : "none",
                  p: isMobile ? 1.5 : 0,
                  mb: isMobile ? 1.5 : 0,
                }}
              >
                <Stack spacing={isMobile ? 1.5 : 2.5}>
                  <TextField
                    label="Title"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    fullWidth
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                    sx={fieldSx}
                  />
                  <TextField
                    label="Message"
                    name="message"
                    value={values.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    multiline
                    minRows={isMobile ? 5 : 4}
                    required
                    fullWidth
                    error={touched.message && Boolean(errors.message)}
                    helperText={touched.message && errors.message}
                    sx={fieldSx}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      pt: isMobile ? 1 : 0,
                      ...(isMobile && {
                        mt: 0.5,
                        borderTop: `1px solid ${BRAND.border}`,
                      }),
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: BRAND.navy,
                          fontWeight: 600,
                          fontSize: isMobile ? "0.9375rem" : "1rem",
                        }}
                      >
                        Active
                      </Typography>
                      {isMobile && (
                        <Typography
                          sx={{
                            color: BRAND.muted,
                            fontSize: "0.8125rem",
                            mt: 0.25,
                            lineHeight: 1.4,
                          }}
                        >
                          Visible to employees when active
                        </Typography>
                      )}
                    </Box>
                    <Switch
                      name="active"
                      checked={values.active}
                      onChange={handleChange}
                      color="primary"
                    />
                  </Box>
                </Stack>
              </Box>
            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
}

export default AddAnnouncement;
